// ──────────────────────────────────────────────────────────────
// 행사 카테고리 분류·만료·사설시리즈 판별 — 단일 진실 공급원
//
// 행사 탭 중복·오분류(구청 공지가 축제로/축제가 공지로)가 반복 버그였어서
// 모듈로 분리 + 회귀 테스트(scripts/test-category.js)로 박제.
// isExpired는 테스트 위해 기준시각(now) 선택 인자 지원.
// (2026-06-18 모듈화 6단계)
// ──────────────────────────────────────────────────────────────

// 순수 "축제·행사" 성격 카테고리 (이 셋이면 행사 탭에 노출)
const PURE_FESTIVAL_CATS = new Set(["축제", "공연·전시", "행사"]);

// 사설(민간) 음악영재·독주회 시리즈 — 공공 행사 아님 → 제외
function isPrivateSeries(e) {
  const txt = `${(e && (e.title || e.name)) || ""} ${(e && (e.location || e.place)) || ""} ${(e && e.organizer) || ""}`;
  if (/\[\s*(라율|예원|선화|서울예술|국립예원|예술영재)/.test(txt)) return true;
  if (/영재\s*[&·]\s*영아티스트|영아티스트\s*[&·]\s*영재/.test(txt)) return true;
  if (/라율아트홀/.test(txt)) return true;
  if (/(첼로|바이올린|피아노|비올라|플루트|클라리넷)\s*독주회/.test(txt)) return true;
  return false;
}

// 카테고리 라벨 정규화 — 백엔드/스크래핑 라벨 표준화. 알 수 없으면 '행사'.
function normCategory(raw) {
  const c = String(raw || "").trim();
  if (!c) return "행사";
  if (/축제|페스티벌|페스티발|페스타|테마파크/.test(c)) return "축제";
  if (/공연|전시|음악회|콘서트|독주회|연주회|뮤지컬|금요/.test(c)) return "공연·전시";
  if (/강좌|교실|아카데미|클래스|특강|상담/.test(c)) return "강좌";
  if (/모집|선발|접수|공모/.test(c)) return "모집";
  if (/공지|행정|안내|소식|뉴스|보도/.test(c)) return "공지";
  if (/행사|이벤트|체험|견학|대회|프로그램/.test(c)) return "행사";
  return "행사";
}

// 제목+카테고리 종합 판정 — 구청 공지여도 제목이 축제/공연이면 축제·행사로 승격
function effCat(e) {
  const byCat = normCategory((e && (e.category_tag || e.category)) || "");
  if (PURE_FESTIVAL_CATS.has(byCat)) return byCat;
  const t = String((e && (e.title || e.name)) || "");
  if (/축제|페스티벌|페스티발|페스타|문화제|불꽃|벚꽃|등불|등축제|음악제|영화제|박람회|한마당|축전|어울림마당/.test(t)) return "축제";
  if (/공연|전시회|전시|음악회|콘서트|뮤지컬|연주회|독주회|오페라|발레|국악한마당/.test(t)) return "공연·전시";
  return byCat;
}

// 날짜 문자열 → Date. YYYYMMDD·YYYY-MM-DD·YYYY.MM.DD·YYYY/MM/DD 모두 인식.
// (구버전은 length>=10만 처리해 TourAPI·KCISA의 8자리 YYYYMMDD를 통째로 건너뛰었다 — 2026-08-06 감사)
function parseEventDate(s) {
  const digits = String(s || "").replace(/[^0-9]/g, "").slice(0, 8);
  if (digits.length !== 8) return null;
  const d = new Date(`${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}T00:00:00`);
  return isNaN(d) ? null : d;
}

// 종료된 행사인가? endDate가 지났거나, endDate 없으면 startDate + 1일 경과 시 종료.
// 유예를 14일→1일로 축소: 구청 행사달력은 대부분 단일일 행사라 14일 유예가
// "지난 금요음악회·백일장"을 계속 노출시켰다 (2026-08-06 사장님 지적·감사 확정).
function isExpired(e, now) {
  const today = now ? new Date(now) : new Date();
  today.setHours(0, 0, 0, 0);
  const end = parseEventDate(e && e.endDate);
  if (end) return end < today;
  const start = parseEventDate(e && (e.startDate || e.date));
  if (start) { const diff = Math.floor((today - start) / (1000 * 60 * 60 * 24)); return diff > 1; }
  return false; // 날짜 정보 없으면 상시로 보고 통과
}

// 주제 테마 분류·정렬 — 구청 공지를 뒤죽박죽이 아니라 가득 앱 취지(가족) 순으로 정렬 (2026-06-21)
// 순서: 아동·교육 → 가족 → 청년 → 여성 → 행정. 같은 테마 내에서는 시작일 가까운 순.
const THEME_ORDER = ["아동·교육", "가족", "청년", "여성", "행정"];
function eventTheme(e) {
  const txt = `${(e && (e.title || e.name)) || ""} ${(e && e.target) || ""} ${(e && (e.category_tag || e.category)) || ""}`;
  // 가족 앱이므로 아동·교육을 가장 앞에. 검사 순서 = 우선순위.
  if (/아동|어린이|유아|영유아|키즈|초등|중등|고등|중학|고교|청소년|학생|교육|학습|돌봄|보육|진로|입시|독서|도서관/.test(txt)) return "아동·교육";
  if (/가족|부모|육아|양육|다자녀|임신|출산|신혼|아기|엄마|아빠|부부|어버이|효/.test(txt)) return "가족";
  if (/청년|취업|창업|구직|일자리|인턴|커리어|대학생|사회초년|구인/.test(txt)) return "청년";
  if (/여성|임산부|모성|경력단절|여성가족/.test(txt)) return "여성";
  return "행정"; // 나머지(행정·생활·건강·체육·환경·주민·일반)
}
function themeRank(e) {
  const i = THEME_ORDER.indexOf(eventTheme(e));
  return i < 0 ? THEME_ORDER.length : i;
}
// 행사 목록 정렬 — 테마 순 → 같은 테마는 시작일(가까운 것 우선, 날짜 없으면 뒤로)
function sortByTheme(arr) {
  return [...(arr || [])].sort((a, b) => {
    const ta = themeRank(a), tb = themeRank(b);
    if (ta !== tb) return ta - tb;
    const da = String((a && (a.startDate || a.date)) || "9999-99-99");
    const db = String((b && (b.startDate || b.date)) || "9999-99-99");
    return da.localeCompare(db);
  });
}

module.exports = { PURE_FESTIVAL_CATS, isPrivateSeries, normCategory, effCat, isExpired, THEME_ORDER, eventTheme, sortByTheme };
